import { defineCopy } from '@floorfile/i18n'

/**
 * Safety: incident reporting, investigation, help requests and the TRIR
 * denominator (localization plan Phase 5a).
 *
 * ## `type`, `status` and `severity` moved here out of `lib.ts`
 *
 * They were `Record<IncidentType, string>` constants in `features/incidents/lib.ts`
 * — the shape every slice in this repo uses. A label map in a `lib.ts` is
 * unreachable from a catalogue and unreachable from a translator, so the words
 * were permanently English no matter what locale was active. The maps keep
 * their enum keys, so an added `IncidentType` still fails the build.
 *
 * `severity` is new: the page rendered it as
 * `value.charAt(0) + value.slice(1).toLowerCase()`, which is a translation
 * expressed as string surgery — see the note on `downtime/copy.ts`.
 *
 * ## `typeInline` and `statusInline` exist because `.toLowerCase()` does not
 *
 * Two empty states composed `No ${TYPE_LABELS[t].toLowerCase()} incidents` and
 * `No ${STATUS_LABELS[s].toLowerCase()} requests`. Lowercasing a translated
 * noun imposes English orthography — German capitalises nouns mid-sentence,
 * and French lowercases words English capitalises — and it is invisible in an
 * English review because English happens to agree. The sentence forms are
 * whole messages instead, so the translator controls both the case and the
 * word order.
 */
export const incidentsCopy = defineCopy('incidents', {
  offlineAction: {
    report: 'Report incident',
    updateDetails: 'Update incident details',
    requestHelp: 'Request help',
  },
  page: {
    title: 'Safety',
    description: 'Fast, honest reporting in; disciplined investigation and a tamper-proof record out.',
    daysSinceRecordable: 'Days since last recordable',
    daysSinceLostTime: 'Days since last lost-time',
    /** FF-QA-027: the figure when nothing of that kind was ever recorded. */
    daysNone: 'None recorded',
    region: 'Incidents',
    /**
     * Tab LABELS, keyed by the tab's own id.
     *
     * The ids stay English because they are the URL slug and the state value —
     * `?tab=hours-worked` is an address, not a word. Only the label is
     * translated, which is the same separation `app/copy.ts` makes for nav
     * entries keyed by route path.
     */
    tabs: 'Safety views',
    tab: {
      Incidents: 'Incidents',
      // `Stats`, not `Statistics`: Phase 5 externalizes strings, it does not
      // reword them. English output stays byte-identical so the e2e suite's
      // text locators keep working as a free regression net.
      Stats: 'Stats',
      'Hours worked': 'Hours worked',
    },
    typeFilter: 'Type: {type}',
    oshaFilter: 'OSHA recordable',
    clearFilter: 'Clear filter',
    /** Empty states. Whole sentences — see the header on `typeInline`. */
    emptyAll: 'No incidents',
    emptyOfType: 'No {type} incidents',
    /** MET-001 (owner, 2026-08-16): "Pareto" names the chart METHOD, not the
     * question the reader came with. The bars are still sorted most-common
     * first; the ordering is a property of the chart, not of its heading. */
    paretoHeading: 'Incidents by type',
    paretoHint: 'Select a bar to inspect its source records.',
    byMonth: 'By month',
    /**
     * The period both the Incidents and Stats tabs read (owner, 2026-09-23).
     * `from`/`to` are the same URL keys the Safety dashboard's drill-throughs
     * already sent — they were applied, just never shown.
     */
    periodFrom: 'Occurred from',
    periodTo: 'Occurred to',
    /** FF-QA-176 */
    search: 'Search incidents…',
    statusFilter: 'Status',
    periodShown: 'Showing {from} – {to}',
    periodDefault: 'Showing {from} – {to}, the default window. Set Occurred from / to under Filters to change it.',
    /** Stats KPI tiles — were English literals in the page. */
    kpi: {
      incidents: 'Incidents',
      nearMisses: 'Near misses',
      recordables: 'Recordables',
      lostTime: 'Lost-time cases',
      trir: 'TRIR',
      ltifr: 'LTIFR',
      hoursMissing: 'Enter hours worked to calculate',
    },
    loadingIncidents: 'Loading incidents…',
    emptyOfTypeHint: 'Clear the filter to return to all source records.',
    emptyAllHint: 'Reports are cheap, silence is expensive — the button is always one tap away.',
    /** A type that is not itself Near miss but was flagged as one. */
    nearMissSuffix: '{type} (near miss)',
    lockedTitle: 'Approved & locked',
    oshaTitle: 'OSHA recordable',
    barLabel: 'Show {count} {type} incidents',
    column: {
      number: 'Number',
      type: 'Type',
      title: 'Title',
      facility: 'Facility',
      severity: 'Severity',
      occurred: 'Occurred',
      status: 'Status',
    },
    /**
     * Lowercase: read inside "Sorted by {field}, ascending".
     *
     * Chunk 5a-2 left this call site passing two inline English literals
     * (`{ incidentNumber: 'number', occurredAt: 'occurred' }`) and letting the
     * other four sortable columns fall through to `humanizeSortField`, which
     * splits the API identifier. In French a screen reader heard "Trié par
     * number" and "Trié par severity". English is unchanged — the fallback
     * happened to produce these exact words, which is why nothing looked wrong.
     * **One entry per SORTABLE column.**
     */
    sortField: {
      incidentNumber: 'number',
      type: 'type',
      title: 'title',
      severity: 'severity',
      occurredAt: 'occurred',
      status: 'status',
    },
  },

  /**
   * The incident types as they read INSIDE a sentence.
   *
   * Not `type.INJURY.toLowerCase()` — case is a property of the language.
   */
  typeInline: {
    INJURY: 'injury',
    NEAR_MISS: 'near miss',
    PROPERTY_DAMAGE: 'property damage',
    ENVIRONMENTAL: 'environmental',
    VEHICLE_ACCIDENT: 'vehicle accident',
    EQUIPMENT_FAILURE: 'equipment failure',
    HAZARDOUS_EXPOSURE: 'hazardous exposure',
    ERGONOMIC_ISSUE: 'ergonomic issue',
    OTHER: 'other',
  },

  report: {
    title: 'Report incident',
    typeGroup: 'Incident type',
    headline: 'What happened, in one line',
    headlinePlaceholder: 'What happened, in one line*',
    detail: 'What happened, in your words',
    detailPlaceholder: 'What happened, in your words*',
    location: 'Location',
    locationPlaceholder: 'Where?*…',
    locationUnavailable: 'Locations unavailable — could not load',
    severity: 'Severity',
    /** FF-AUDIT-20260921-210633-023: when it happened, if that is not now. */
    occurredAt: 'When it happened (leave blank for now)',
    workerInvolved: 'Worker involved',
    injuryType: 'Injury type (e.g. Laceration)',
    bodyPart: 'Body part',
    addPhotos: 'Add photos',
    removePhoto: 'Remove {fileName}',
    wasNearMiss: 'It was a near miss',
    anonymously: 'Report anonymously',
    anonymousNote: 'Your name will not be attached to this report.',
    cancel: 'Cancel',
    submit: 'Submit report',
    submitting: 'Reporting…',
    /** FF-AUDIT-017: what a disabled Submit is waiting for. */
    missing: 'To submit, add: {fields}.',
    missingJoiner: ', ',
    missingTitle: 'a one-line title',
    missingDetail: 'what happened',
    missingLocation: 'where',
    missingWorker: 'the worker involved',
    failed: 'Could not report.',
    /** Confirmation. `PENDING` is the offline case — a real, different state. */
    doneTitle: 'Reported as {number}',
    doneDescription: 'Supervisors have been notified.',
    pendingTitle: 'Saved on this device',
    pendingDescription:
      'You are offline — the report will send and get its number the moment you reconnect.',
    done: 'Done',
    photosAttached: {
      one: '{count} photo attached.',
      other: '{count} photos attached.',
    },
    photosPartial: '{uploaded}/{total} photos uploaded — retry the rest from the incident page.',
  },

  /**
   * The optional 5×5 EHS risk matrix — UI plan Phase 8, workstream 4.
   *
   * One group, read by four surfaces: the report dialog, the incident detail,
   * the register's band filter and the Safety dashboard's grid. The same words
   * everywhere on purpose — a plant that calls it "exposure" on one screen and
   * "consequence" on another has two matrices as far as its people are
   * concerned.
   *
   * ## Why the axis labels carry a sentence each
   *
   * `Likelihood` and `Exposure` are not self-explanatory to somebody filling in
   * a form ten minutes after an injury, and getting the axes the wrong way
   * round produces a plausible-looking score that is simply wrong. The 1 and 5
   * anchors are spelled out for the same reason: a bare 1–5 select invites
   * everyone to pick 3.
   */
  risk: {
    heading: 'Risk assessment',
    optional: 'Optional',
    saveFailed: 'Could not save the risk assessment.',
    likelihood: 'Likelihood',
    likelihoodHelp: 'How likely is this to happen again? 1 = very unlikely, 5 = almost certain.',
    exposure: 'Exposure',
    exposureHelp: 'How bad could the outcome be? 1 = negligible, 5 = catastrophic.',
    notAssessed: 'Not assessed',
    /** The product, spelled out so nobody has to guess how the number arose. */
    scoreLine: '{likelihood} × {exposure} = {score}',
    band: {
      LOW: 'Low risk',
      MEDIUM: 'Medium risk',
      HIGH: 'High risk',
      CRITICAL: 'Critical risk',
    },
    /** The register's filter. */
    filterAll: 'Any risk',
    filterLabel: 'Risk band',
    /** The Safety dashboard's grid. */
    matrixHeading: 'Risk matrix',
    matrixDescription: 'Where this period’s assessed incidents sit on the 5×5 grid.',
    matrixLikelihoodAxis: 'Likelihood →',
    matrixExposureAxis: 'Exposure ↑',
    /**
     * Printed beside the grid. A matrix drawn from a third of the incidents
     * reads as the whole picture unless it says otherwise — this is the
     * sentence that stops a plant concluding it has almost no risk when what it
     * has is almost no assessments.
     */
    unscored: {
      one: '{count} incident in this period has no assessment.',
      other: '{count} incidents in this period have no assessment.',
    },
    /** A cell's accessible name — the grid is otherwise a wall of numbers. */
    cell: '{count} at likelihood {likelihood}, exposure {exposure}',
    cellEmpty: 'None at likelihood {likelihood}, exposure {exposure}',
  },

  help: {
    title: 'Request help',
    pageTitle: 'Help requests',
    pageDescription:
      'Non-emergency assistance on the floor — a qualified person, a quality inspection, an extra set of hands.',
    request: 'Request help',
    need: 'What do you need?',
    needPlaceholder: 'What do you need? (optional)',
    where: 'Where are you?',
    wherePlaceholder: 'Where are you? (pick a location)',
    extraDetail: 'Extra detail — e.g. behind the racks',
    extraDetailPlaceholder: 'Extra detail — e.g. behind the racks (optional)',
    /** FF-AUDIT-069: the picked location and the detail share one 150-character
     * field; the room left is shown so a long detail is caught before sending. */
    extraDetailRoom: { one: '{count} character left with this location', other: '{count} characters left with this location' },
    sending: 'Sending…',
    sendNow: 'Send — supervisors are told instantly',
    sendQueued: 'Queue — sends on reconnect',
    failed: 'Could not send.',
    /** The dialog closes on a live send; this is what says it went (FF-QA-106's
     * third symptom — documents and workers got theirs, help requests did not). */
    sent: 'Help requested — supervisors have been alerted.',
    /** Shown INSTEAD of closing when the request went to the offline queue —
     * nobody has been told yet, and the dialog must not imply otherwise. */
    queuedNote: 'Queued on this device — supervisors will be alerted the moment you reconnect. You can see or discard it under the connection status.',
    close: 'Close',
    urgent: 'Urgent (admins are escalated too)',
    /** DEGRADED offline (D24 §2): honest caveat plus a physical fallback. */
    offlineNote:
      "You're offline — this will alert supervisors the moment you reconnect. If it's an emergency, use the andon cord or call out.",
    cancel: 'Cancel',
    emptyActive: 'Nobody needs help right now',
    /** Whole sentence, not `No ${status.toLowerCase()} requests`. */
    emptyOfStatus: 'No {status} requests',
    statusTabs: 'Help request status',
    emptyActiveDescription: 'Requests pop up here live for every supervisor.',
    emptyHandledDescription: 'Requests move here once handled.',
    resolve: 'Resolve',
    resolvedAt: 'Resolved {at}',
    /** FF-AUDIT-052: the resolver's name, an optional note, and an age in words. */
    resolvedByAt: 'Resolved by {name} {at}',
    resolvedNote: ' — {note}',
    resolveTitle: 'Resolve {name}’s request',
    resolveBody: 'Marks the request handled and records your name on it.',
    resolveNote: 'Note (optional)',
    resolveNotePlaceholder: 'What was done (optional)…',
    ageJustNow: 'just now',
    ageDays: { one: '{count} d', other: '{count} d' },
    status: {
      ACTIVE: 'Active',
      RESOLVED: 'Resolved',
      CANCELLED: 'Cancelled',
    },
    statusInline: {
      ACTIVE: 'active',
      RESOLVED: 'resolved',
      CANCELLED: 'cancelled',
    },
  },

  detail: {
    lockedBanner: '🔒 Approved & locked {when} — read only.',
    lockedOn: 'on {at}',
    oshaRecordable: 'OSHA recordable',
    lostTime: 'Lost time',
    evidenceBundle: 'Evidence bundle',
    investigator: 'Investigator',
    startInvestigation: 'Start investigation',
    completeInvestigation: 'Complete investigation',
    requireCar: 'Require corrective action',
    close: 'Close',
    approveAndLock: 'Approve & lock',
    /** FF-AUDIT-20260914-210459-088: close, approve and reopen are confirmed in-app (no native confirm/prompt). */
    /** FF-QA-162: the lock is reversible by an administrator with a logged reason — say so. */
    approveConfirm: 'This locks the incident. Only an administrator can reopen it, with a logged reason. Approve?',
    /** FF-AUDIT-20260921-210633-024: only admins see the button, so the label does not need the role code. */
    reopen: 'Reopen',
    closeDialog: {
      title: 'Close {number}?',
      description: 'Closing records the incident as dealt with. The investigation stays as written and can no longer be edited; an ADMIN can reopen it with a logged reason.',
      note: 'Closure statement (optional)',
      notePlaceholder: 'e.g. Guard refitted and verified on the 14:00 walk-round.',
      cancel: 'Keep open',
      confirm: 'Close incident',
      closing: 'Closing…',
    },
    approveDialog: {
      title: 'Approve and lock {number}?',
      cancel: 'Not yet',
      confirm: 'Approve & lock',
    },
    reopenDialog: {
      title: 'Reopen {number}?',
      description: 'The reason is permanently logged on the record and in the audit trail.',
      reason: 'Reason (required)',
      cancel: 'Keep closed',
      confirm: 'Reopen',
    },
    closedSummary: 'closed after {days}d',
    closedBy: 'Closed by {name}, {when}',
    /** The colon is part of the sentence — French sets a space before it. */
    closureNote: 'Closure statement:',
    closedReadOnly: 'This incident is closed. The investigation is read-only; an ADMIN can reopen it with a logged reason.',
    save: 'Save',
    /** FF-AUDIT-015: the CAR by number, its status in the CAR catalogue's words. */
    carLinked: '{number} · {status} →',
    carNoNumber: 'CAR linked',
    /** FF-AUDIT-20260914-210459-014: what "Require corrective action" just did, on the page, at once. */
    carCreated: '{number} created and assigned to {assignee}. The incident now waits for that corrective action to be verified.',
    carCreatedUnassigned: '{number} created; nobody could be assigned automatically — open it to assign.',
    carStatusPending: '…',
    investigationHeading: 'Investigation {investigator}',
    noInvestigator: '(no investigator yet)',
    /** FF-AUDIT-20260914-210459-018: the name, once assigned. */
    investigatorNamed: '— {name}',
    /** FF-AUDIT-20260914-210459-018: the 422 keyed on its code, with the fields named in the reader's language. */
    investigationIncomplete: 'Record the investigation first. Still missing: {fields}.',
    /** FF-AUDIT-20260914-210459-012: the NCR is a side link on a safety incident, not its follow-up. */
    linkNcr: 'Link a quality issue…',
    linkNcrHelp: 'Creates a nonconformance record for material or process found during this incident. The corrective action itself is "Require corrective action".',
    ncrLinked: 'A quality issue is already linked to this incident (see Problem lineage below).',
    oshaCheckbox: 'OSHA recordable',
    reportHeading: 'Report',
    anonymous: 'Anonymous',
    /** The record rail (Fiix audit Phase 2): who / when / where, which the
     * report section used to carry as one sentence under the description. */
    facts: {
      region: 'Incident facts',
      reportedBy: 'Reported by',
      occurred: 'Occurred',
      reported: 'Reported',
      location: 'Location',
      department: 'Department',
      shift: 'Shift',
      injury: 'Injury',
      injuryValue: '{injuryType} ({bodyPart})',
    },
    /** The header line: type, an optional near-miss note, location, age. */
    summary: '{type}{nearMiss} · {location} · {age}',
    openSummary: '{days}d open',
    nearMissSuffix: ' (near miss)',
    rootCause: 'Root cause',
    immediateAction: 'Immediate action taken',
    preventiveAction: 'Preventive action',
    investigationNotes: 'Investigation notes',
    /** Overrides `<AiSuggestionPanel>`'s default "Insert" — the panel sits
     * beside Investigation notes but the text lands in Root cause, so the
     * button has to say which. Was a raw English literal on the prop, which
     * the copy rule cannot see (it is not one of the props it checks). */
    insertAiRootCause: 'Insert into root cause',
    lostTimeSummary: '{hours} h lost · {daysAway} days away · {daysRestricted} restricted',
    lostTimeCheckbox: 'Lost time',
    recordableHint: 'Recordable: death, days away, restricted work, or medical treatment beyond first aid.',
    reopenedNote: ' (Previously reopened: {reason})',
    /** FF-AUDIT-20260921-210633-019: a reopened incident says why, and what happens next. */
    reopenedBanner: 'Reopened — {reason}. Assign an investigator to continue.',
    attachments: 'Attachments',
    addAttachment: 'Add',
    noAttachments: 'No photos or files attached.',
    cancelEdit: 'Cancel',
    /** FF-AUDIT-20260921-210633-024: an empty investigation field read "missingedit" — two English literals with no separator. */
    fieldNotRecorded: 'Not recorded',
    editField: 'Edit',
  },

  involvement: {
    heading: 'Involvement',
    description: 'Injured people, others involved, witnesses, and equipment.',
    empty: 'Nobody and no equipment recorded yet.',
    role: 'Role',
    subject: 'Subject',
    subjectWorker: 'Worker',
    subjectEquipment: 'Equipment',
    worker: 'Worker',
    searchEquipment: 'Search equipment',
    equipment: 'Equipment',
    equipmentSearch: 'Name, asset tag, or serial…',
    equipmentPlaceholder: 'Choose equipment…',
    equipmentOption: '{name} · {assetTag}',
    note: 'Note (optional)',
    notePlaceholder: 'Context, injury detail, witness relationship…',
    add: 'Add involvement',
    unknownSubject: 'Unknown subject',
    removeLabel: 'Remove {subject}',
    removeFallback: 'involvement',
    count: '{used}/{max}',
  },

  hours: {
    whyHeading: 'Why this page exists',
    whyBody:
      'TRIR is recordable incidents × 200,000 ÷ hours worked. FloorFile cannot derive the hours — attendance only covers people who clock in here — so the rate stays unavailable rather than being estimated from a denominator that is too small.',
    gapsRegion: 'Months with no hours recorded',
    gapsHeading: {
      one: '{count} month with no figure',
      other: '{count} months with no figure',
    },
    gapsBody: 'TRIR is suppressed for any range covering these months.',
    gapAction: ' — enter hours for this month',
    recordHeading: 'Record hours worked',
    month: 'Month',
    facility: 'Facility',
    companyWide: 'Company-wide',
    hoursWorked: 'Hours worked',
    /** FF-AUDIT-026: a blank is NOT a zero — an unreported month must never become a 0 denominator. */
    hoursPlaceholder: 'e.g. 41600',
    hoursBlank: 'Enter the hours worked for the month. Leave nothing here if there is no figure yet — a blank is not zero.',
    hoursInvalid: 'Enter the hours worked as a number of hours, zero or more.',
    saving: 'Saving…',
    saveFigure: 'Save figure',
    saveFailed: 'Could not save that figure.',
    note: 'Note (optional)',
    notePlaceholder: 'Source of the figure',
    empty: 'No hours have been recorded yet. TRIR stays unavailable until at least one month has a figure.',
    remove: 'Remove',
    tableCaption: 'Hours worked by facility and month',
    actions: 'Actions',
    replaceWarning: 'Re-entering a month replaces its figure and withdraws any sign-off.',
    legacySource: 'from the legacy company setting',
    column: {
      month: 'Month',
      facility: 'Facility',
      hours: 'Hours',
      status: 'Status',
      /** F23 */
      source: 'Source',
    },
    // ── F23: standard monthly hours ─────────────────────────────────────
    monthsHeading: 'Hours by month',
    monthsBody: 'Each month as the safety rates read it: the figure entered for it, or else the standard in effect.',
    recordHours: '+ Record hours',
    recordTitle: 'Record hours worked',
    recordDescription: 'A figure for one month. It replaces the standard for that month only.',
    cancel: 'Cancel',
    source: {
      MANUAL: 'Entered',
      IMPORT: 'Imported',
      LEGACY: 'Old company setting',
      STANDARD: 'Standard',
    },
    verified: 'Verified',
    unverified: 'Unverified',
    notSigned: '—',
    verify: 'Verify',
    withdraw: 'Withdraw sign-off',
    override: 'Override this month',
    revert: 'Revert to standard',
    revertTitle: 'Revert {month} to the standard?',
    revertBody: 'The figure entered for this month is removed, and the month uses the standard of {hours} hours again.',
    removeTitle: 'Remove the figure for {month}?',
    removeBody: 'No standard covers this month, so it will have no figure and TRIR for ranges including it is suppressed.',
    rowMenu: 'Actions for {month}, {facility}',
    standardsHeading: 'Standard monthly hours',
    standardsBody:
      'Enter a normal month once per facility. Every month without its own figure uses the standard in effect then, so you only enter unusual months. Changing a standard from a later month never rewrites earlier ones.',
    newStandard: '+ New standard',
    newStandardTitle: 'New standard monthly hours',
    editStandardTitle: 'Edit standard monthly hours',
    standardDescription: 'Applies from the chosen month onwards, until a later standard takes over.',
    monthlyHours: 'Standard monthly hours',
    effectiveFrom: 'Applies from',
    standardsEmpty: 'No standard yet. Without one, every month needs its own figure.',
    standardsCaption: 'Standard monthly hours by facility',
    column2: {
      facility: 'Facility',
      monthlyHours: 'Monthly hours',
      effectiveFrom: 'Applies from',
      note: 'Note',
    },
    editStandard: 'Edit',
    deleteStandard: 'Delete',
    deleteStandardTitle: 'Delete the standard from {month}?',
    deleteStandardBody: 'Months it covered use the previous standard, or have no figure if there is none.',
    standardRowMenu: 'Actions for the standard from {month}, {facility}',
    save: 'Save',
  },

  quickAction: {
    requestHelp: 'Request help',
    reportIncident: 'Report incident',
  },
})

export type IncidentsCopy = typeof incidentsCopy.en
